import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Message,
  Icon,
  Header,
  Label,
  Segment,
  Card,
} from 'semantic-ui-react';
import GlassPageWrapper from '../../../components/Layout/GlassPageWrapper';
import { quizAttemptService, QuizAttemptDto, AnswerDto } from '../../../services/learning/QuizAttemptService';
import { quizService, QuizDto, QuestionDto, QuestionOptionDto } from '../../../services/learning/QuizService';
import { useAuth } from '../../../context/AuthContext';

const QuizReviewPage: React.FC = () => {
  const { quizId, attemptId } = useParams<{ quizId: string; attemptId: string }>();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();

  const [quiz, setQuiz] = useState<QuizDto | null>(null);
  const [attempt, setAttempt] = useState<QuizAttemptDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdminView = hasRole('Admin');

  useEffect(() => {
    const loadData = async () => {
      if (!quizId || !attemptId) {
        setError('Identificadores de questionário ou tentativa inválidos.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Carregar tentativa e questionário em paralelo
        const [attemptData, quizData] = await Promise.all([
          quizAttemptService.getQuizAttempt(attemptId),
          quizService.getQuiz(quizId),
        ]);

        setAttempt(attemptData);
        setQuiz(quizData);

        // Checagem simples de autorização no front:
        // usuário comum só pode ver suas próprias tentativas
        if (!hasRole('Admin') && !hasRole('Supervisor')) {
          if (attemptData.userId !== user?.id) {
            setError('Você não tem permissão para visualizar esta tentativa.');
          }
        }
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar dados da revisão do questionário.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [quizId, attemptId, user?.id, hasRole]);

  const formatTime = (seconds?: number): string => {
    if (!seconds && seconds !== 0) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <GlassPageWrapper title="Revisão do Questionário" icon="clipboard list">
        <Message info>
          <Icon name="spinner" loading />
          Carregando dados da tentativa...
        </Message>
      </GlassPageWrapper>
    );
  }

  if (error || !quiz || !attempt) {
    return (
      <GlassPageWrapper title="Revisão do Questionário" icon="exclamation triangle">
        <Message negative>
          <Message.Header>Não foi possível carregar a revisão</Message.Header>
          <p>{error || 'Questionário ou tentativa não encontrados.'}</p>
          <Message.Content>
            <p style={{ marginTop: '8px' }}>
              Verifique se você acessou este link a partir da página de resultados ou de relatórios.
            </p>
          </Message.Content>
          <div style={{ marginTop: '16px' }}>
            <button
              onClick={() => navigate('/learning')}
              className="ui button"
            >
              <Icon name="arrow left" />
              Voltar ao Centro de Aprendizado
            </button>
          </div>
        </Message>
      </GlassPageWrapper>
    );
  }

  const answerMap = new Map<string, AnswerDto>(
    attempt.answers.map((a) => [a.questionId, a])
  );

  const score = attempt.score ?? 0;
  const isPassed = attempt.isPassed ?? false;

  const renderOption = (question: QuestionDto, option: QuestionOptionDto) => {
    const answer = answerMap.get(question.id);
    const selectedOptionId = answer?.questionOptionId;
    const isSelected = selectedOptionId === option.id;
    const isCorrectOption = option.isCorrect;

    // Usuário normal: só destaca a opção marcada, sem mostrar qual é a correta
    if (!isAdminView) {
      return (
        <div
          key={option.id}
          style={{
            padding: '12px 14px',
            borderRadius: '10px',
            border: `2px solid ${isSelected ? '#2185d0' : 'rgba(0,0,0,0.1)'}`,
            background: isSelected ? '#2185d0' : '#ffffff',
            color: isSelected ? '#ffffff' : '#2c3e50',
            marginBottom: '8px',
            boxShadow: isSelected
              ? '0 4px 12px rgba(33, 133, 208, 0.3)'
              : '0 2px 6px rgba(0,0,0,0.05)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                border: `2px solid ${isSelected ? '#ffffff' : '#2185d0'}`,
                background: isSelected ? '#ffffff' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {isSelected && (
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#2185d0',
                  }}
                />
              )}
            </div>
            <span
              style={{
                fontSize: '0.95em',
                fontWeight: isSelected ? 600 : 400,
                lineHeight: 1.4,
              }}
            >
              {option.text}
            </span>
          </div>
        </div>
      );
    }

    // Admin: pode ver o que é correto e o que foi escolhido
    const isSelectedAndIncorrect = isSelected && !isCorrectOption;
    const isSelectedAndCorrect = isSelected && isCorrectOption;

    let borderColor = 'rgba(0,0,0,0.1)';
    let background = '#ffffff';
    let textColor = '#2c3e50';
    let iconName: 'check circle' | 'times circle' | 'circle outline' = 'circle outline';
    let iconColor: 'green' | 'red' | 'grey' = 'grey';

    if (isCorrectOption) {
      borderColor = '#21ba45';
      background = 'rgba(33, 186, 69, 0.08)';
      textColor = '#1b5e20';
      iconName = 'check circle';
      iconColor = 'green';
    }

    if (isSelectedAndIncorrect) {
      borderColor = '#db2828';
      background = 'rgba(219, 40, 40, 0.08)';
      textColor = '#7f0000';
      iconName = 'times circle';
      iconColor = 'red';
    }

    if (isSelectedAndCorrect) {
      // Mantém visual de correta, apenas reforça selecção
      background = 'rgba(33, 186, 69, 0.16)';
    }

    return (
      <div
        key={option.id}
        style={{
          padding: '12px 14px',
          borderRadius: '10px',
          border: `2px solid ${borderColor}`,
          background,
          color: textColor,
          marginBottom: '8px',
          boxShadow: isSelected
            ? '0 4px 12px rgba(0,0,0,0.12)'
            : '0 2px 6px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icon name={iconName} color={iconColor} />
          <span
            style={{
              fontSize: '0.95em',
              fontWeight: isCorrectOption ? 600 : 400,
              lineHeight: 1.4,
            }}
          >
            {option.text}
          </span>
          {isSelected && (
            <Label
              basic
              color={isSelectedAndIncorrect ? 'red' : isSelectedAndCorrect ? 'green' : 'blue'}
              size="mini"
              style={{ marginLeft: 'auto' }}
            >
              Sua resposta
            </Label>
          )}
          {isCorrectOption && (
            <Label
              color="green"
              size="mini"
              style={{ marginLeft: isSelected ? '6px' : 'auto' }}
            >
              Correta
            </Label>
          )}
        </div>
      </div>
    );
  };

  return (
    <GlassPageWrapper title={`Revisão: ${quiz.title}`} icon="clipboard list">
      <div
        style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '0 20px',
        }}
      >
        {/* Resumo da tentativa */}
        <Segment
          style={{
            background: isPassed ? '#f0fff4' : '#fff5f5',
            borderRadius: '12px',
            border: `1px solid ${isPassed ? '#21ba45' : '#db2828'}`,
            marginBottom: '20px',
          }}
        >
          <Header as="h2" style={{ marginBottom: '10px' }}>
            <Icon
              name={isPassed ? 'check circle' : 'times circle'}
              color={isPassed ? 'green' : 'red'}
            />
            <Header.Content>
              {isPassed ? 'Aprovado' : 'Reprovado'}
              <Header.Subheader>
                Revisão da tentativa de {attempt.userName}
              </Header.Subheader>
            </Header.Content>
          </Header>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              marginTop: '10px',
            }}
          >
            <Label color="blue">
              <Icon name="percent" />
              Nota: {score.toFixed(1)}%
            </Label>
            <Label>
              <Icon name="clock" />
              Tempo gasto: {formatTime(attempt.timeSpentSeconds)}
            </Label>
            {attempt.completedAt && (
              <Label>
                <Icon name="calendar" />
                Concluído em:{' '}
                {new Date(attempt.completedAt).toLocaleString('pt-BR')}
              </Label>
            )}
            {isAdminView && (
              <Label color="purple" basic>
                <Icon name="shield alternate" />
                Modo Admin: respostas corretas destacadas
              </Label>
            )}
          </div>
        </Segment>

        {/* Lista de questões */}
        {quiz.questions.map((question, index) => {
          const answer = answerMap.get(question.id);
          const isQuestionAnswered = !!answer?.questionOptionId;
          const isQuestionCorrect = answer?.isCorrect ?? false;

          return (
            <Card
              key={question.id}
              fluid
              style={{
                marginBottom: '18px',
                borderRadius: '16px',
                boxShadow: isQuestionCorrect
                  ? '0 6px 20px rgba(33, 186, 69, 0.18)'
                  : '0 6px 20px rgba(219, 40, 40, 0.12)',
                border: `2px solid ${
                  isQuestionCorrect ? '#21ba45' : '#db2828'
                }`,
              }}
            >
              <Card.Content>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px',
                    marginBottom: '10px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Label color="blue">
                      Questão {index + 1}
                    </Label>
                    <Label color={isQuestionCorrect ? 'green' : 'red'}>
                      <Icon name={isQuestionCorrect ? 'check' : 'times'} />
                      {isQuestionCorrect
                        ? 'Você acertou esta questão'
                        : 'Você errou esta questão'}
                    </Label>
                    <Label basic>
                      <Icon name="star" />
                      {question.points} ponto
                      {question.points !== 1 ? 's' : ''}
                    </Label>
                  </div>

                  {!isQuestionAnswered && (
                    <Label color="orange" basic>
                      <Icon name="warning circle" />
                      Questão não respondida
                    </Label>
                  )}
                </div>

                <Segment
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    border: '1px solid rgba(0,0,0,0.08)',
                    marginBottom: '14px',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: '1em',
                      lineHeight: 1.5,
                      color: '#2c3e50',
                      fontWeight: 500,
                    }}
                  >
                    {question.text}
                  </p>
                </Segment>

                <div style={{ marginTop: '8px' }}>
                  {question.options.map((option) =>
                    renderOption(question, option)
                  )}
                </div>
              </Card.Content>
            </Card>
          );
        })}

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <button
            className="ui button primary"
            onClick={() => navigate('/learning')}
          >
            <Icon name="arrow left" />
            Voltar ao Centro de Aprendizado
          </button>
        </div>
      </div>
    </GlassPageWrapper>
  );
};

export default QuizReviewPage;

